import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import api from '../../api/axios';
import {PRIMARY_COLOR} from "../../contants/Colors";

const BASE_URL  = process.env.REACT_APP_BASE_URL || window.location.origin;
const API_ROOT  = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');

const SEND_OPTIONS = [
  { value: '',      label: '🔗 Just generate link' },
  { value: 'email', label: '📧 Send via Email' },
  // { value: 'sms',   label: '📱 Send via SMS' },
  // { value: 'both',  label: '📧📱 Send via Both' },
];

const EMOJI_MAP        = { angry:'😠', sad:'😢', neutral:'😐', happy:'😊', excited:'🤩' };
const SENTIMENT_COLOR  = { angry:'#ef4444', sad:'#f97316', neutral:'#94a3b8', happy:'#22c55e', excited:'#8b5cf6' };
const EMPTY = { billId:'', feedbackFormId:'', recipientName:'', recipientEmail:'', recipientPhone:'', sendVia:'', multiResponse: false, locationId: '' };

// ── QR Modal ─────────────────────────────────────────────────────────────────
function QRModal({ link, branding, onClose }) {
  const canvasRef  = useRef(null);
  const [ready, setReady] = useState(false);
  const url = `${BASE_URL}/f/${link.sessionCode}`;

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const SIZE  = 400;
    const LOGO  = 72;
    const PAD   = 16;
    canvas.width  = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');

    // ── 1. Draw QR onto an offscreen canvas ───────────────────────────────
    const offscreen = document.createElement('canvas');
    offscreen.width  = SIZE;
    offscreen.height = SIZE;
    await QRCode.toCanvas(offscreen, url, {
      width: SIZE,
      margin: 2,
      errorCorrectionLevel: 'H', // high — needed for logo overlay
      color: {
        dark:  '#0f0a1a',
        light: '#ffffff',
      },
    });

    // ── 2. Draw QR onto main canvas ───────────────────────────────────────
    ctx.drawImage(offscreen, 0, 0);

    // ── 3. White circle behind logo ───────────────────────────────────────
    // const cx = SIZE / 2;
    // const cy = SIZE / 2;
    // const r  = LOGO / 2 + PAD / 2;
    // ctx.beginPath();
    // ctx.arc(cx, cy, r, 0, Math.PI * 2);
    // ctx.fillStyle = '#ffffff';
    // ctx.fill();

    // ── 4. Draw logo or brand initial ─────────────────────────────────────
    // const logoUrl = branding?.logoUrl ? `${API_ROOT}${branding.logoUrl}` : null;
    // const primaryColor = branding?.primaryColor || 'rgb(255,46,88)';
    //
    // if (logoUrl) {
    //   try {
    //     await new Promise((resolve, reject) => {
    //       const img = new Image();
    //       img.crossOrigin = 'anonymous';
    //       img.onload = () => {
    //         // Clip to circle
    //         ctx.save();
    //         ctx.beginPath();
    //         ctx.arc(cx, cy, LOGO / 2, 0, Math.PI * 2);
    //         ctx.clip();
    //         ctx.drawImage(img, cx - LOGO/2, cy - LOGO/2, LOGO, LOGO);
    //         ctx.restore();
    //         resolve();
    //       };
    //       img.onerror = reject;
    //       img.src = logoUrl;
    //     });
    //   } catch {
    //     // Fall back to initial if logo fails
    //     drawInitial(ctx, cx, cy, LOGO, primaryColor, branding?.businessName || '?');
    //   }
    // } else {
    //   drawInitial(ctx, cx, cy, LOGO, primaryColor, branding?.businessName || '?');
    // }

    setReady(true);
  }, [url, branding]);

  const drawInitial = (ctx, cx, cy, size, color, name) => {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${size * 0.4}px system-ui`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.charAt(0).toUpperCase(), cx, cy);
    ctx.restore();
  };

  useEffect(() => { draw(); }, [draw]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.download = `qr-${link.sessionCode}${link.multiResponse ? '-kiosk' : ''}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  };

  return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }} onClick={onClose}>
        <div style={{
          background: 'var(--surface)', borderRadius: 20, padding: 32,
          maxWidth: 480, width: '100%', textAlign: 'center',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }} onClick={e => e.stopPropagation()}>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start'}}>
              <h3 style={{ margin:0}}>QR Code</h3>
              <p style={{ fontSize:12, color:'var(--text-muted)', margin: '4px 0px 0px'}}>
                {link.multiResponse ? '♾️ Multi-response - stays open forever' : '1️⃣ Single-response'}
              </p>
            </div>
            <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
          </div>

          {/* QR Canvas */}
          <div style={{ display:'inline-block', borderRadius:16, overflow:'hidden', border:'2px solid var(--border)', marginBottom:20 }}>
            <canvas ref={canvasRef} style={{ display:'block', width:280, height:280 }} />
          </div>

          {!ready && (
              <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>Generating QR code…</p>
          )}

          {/* URL label */}
          <div style={{ background:'var(--bg)', borderRadius:8, padding:'8px 14px', marginBottom:20, wordBreak:'break-all' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>{url}</span>
          </div>

          {/* Actions */}
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!ready}
            >
              ⬇️ Download PNG
            </button>
            <button
                className="btn btn-outline"
                onClick={() => { navigator.clipboard.writeText(url); }}
            >
              📋 Copy link
            </button>
            <button
                className="btn btn-outline"
                onClick={() => {
                  const printWin = window.open('', '_blank');
                  const canvas = canvasRef.current;
                  printWin.document.write(`
                <html><body style="text-align:center;padding:40px;font-family:system-ui">
                  <img src="${canvas.toDataURL()}" style="width:300px;height:300px" /><br/>
                  <p style="font-size:14px;color:#666;margin-top:16px">Scan to leave feedback</p>
                  <p style="font-size:12px;color:#999">${url}</p>
                </body></html>
              `);
                  printWin.document.close();
                  printWin.print();
                }}
            >
              🖨️ Print
            </button>
          </div>

          {link.multiResponse && (
              <div style={{ marginTop:20, background:'#dbeafe', border:'1px solid #bfdbfe', borderRadius:10, padding:'10px 14px' }}>
                <p style={{ fontSize:12, color:'#1d4ed8', margin:0, fontWeight:600 }}>
                  ♾️ Kiosk / Display mode
                </p>
                <p style={{ fontSize:11, color:'#1d4ed8', margin:'4px 0 0' }}>
                  This QR code accepts unlimited responses. Perfect for placing at exits, counters, reception areas, or any customer interaction point.
                </p>
              </div>
          )}
        </div>
      </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ManageLinks() {
  const [links, setLinks]       = useState([]);
  const [forms, setForms]       = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [sendErrors, setSendErrors] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [resending, setResending] = useState(null);
  const [qrLink, setQrLink]     = useState(null);
  const [locations, setLocations] = useState([]);


  const load = async () => {
    setLoading(true);
    try {
      const [linksRes, formsRes, brandingRes] = await Promise.all([
        api.get('/links'),
        api.get('/tenant/forms'),
        api.get('/branding').catch(() => ({ data: { data: null } })),
      ]);
      api.get('/locations').then(r => setLocations(r.data.data.filter(l => l.isActive))).catch(() => {});
      setLinks(linksRes.data.data);
      setForms(formsRes.data.data.filter(f => f.isActive));
      // console.log(formsRes.data.data.filter(f => f.isActive))
      setBranding(brandingRes.data.data);
      api.get('/locations').then(r => setLocations(r.data.data.filter(l => l.isActive))).catch(() => {});
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const needsEmail = form.sendVia === 'email' || form.sendVia === 'both';
  const needsPhone = form.sendVia === 'sms'   || form.sendVia === 'both';

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSendErrors([]);
    setCreating(true);
    try {
      const payload = {
        billId:         form.billId || null,
        feedbackFormId: form.feedbackFormId || null,
        recipientName:  form.recipientName || null,
        recipientEmail: needsEmail ? form.recipientEmail : null,
        recipientPhone: needsPhone ? form.recipientPhone : null,
        sendVia:        form.sendVia || null,
        multiResponse:  form.multiResponse,
        locationId: form.locationId || null,
      };
      const { data } = await api.post('/links', payload);
      setSuccess(
          form.sendVia
              ? `Link created & sent! ${data.sendErrors ? '(with some errors)' : ''}`
              : 'Link generated successfully!'
      );
      if (data.sendErrors) setSendErrors(data.sendErrors);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create link');
    } finally { setCreating(false); }
  };

  const handleCopy = (link) => {
    navigator.clipboard.writeText(`${BASE_URL}/f/${link.sessionCode}`);
    setCopiedId(link._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try { await api.delete(`/links/${id}`); load(); }
    catch { setError('Delete failed'); }
  };

  const handleResend = async (link, sendVia) => {
    setResending(link._id);
    try { await api.post(`/links/${link._id}/resend`, { sendVia }); setSuccess('Resent!'); }
    catch { setError('Resend failed'); }
    finally { setResending(null); }
  };

  const f = field => ({ value: form[field], onChange: e => setForm({ ...form, [field]: e.target.value }) });

  return (
      <div>
        {/* QR Modal */}
        {qrLink && (
            <QRModal
                link={qrLink}
                branding={branding}
                onClose={() => setQrLink(null)}
            />
        )}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3>Share Links</h3>
            <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:3 }}>
              Generate shareable feedback links or QR codes for your locations.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setForm(EMPTY); }}>
            {showForm ? 'Cancel' : '+ New Link'}
          </button>
        </div>

        {error   && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {sendErrors.length > 0 && (
            <div className="alert" style={{ background:'#fef9c3', border:'1px solid #fde68a', color:'#92400e' }}>
              {sendErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
        )}

        {showForm && (
            <div className="card mb-3">
              <h4 style={{ marginBottom:18 }}>Create new link</h4>
              <form onSubmit={handleCreate}>

                {/* Multi-response toggle — PROMINENT */}
                <div style={{
                  background: form.multiResponse ? '#eff6ff' : 'var(--bg)',
                  border: `2px solid ${form.multiResponse ? '#3b82f6' : 'var(--border)'}`,
                  borderRadius: 12, padding:'14px 16px', marginBottom:18, cursor:'pointer',
                  transition:'all .2s',
                }} onClick={() => setForm({ ...form, multiResponse: !form.multiResponse })}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{
                      width:44, height:24, borderRadius:12, position:'relative', transition:'background .2s',
                      background: form.multiResponse ? '#3b82f6' : 'var(--border)',
                      flexShrink:0,
                    }}>
                      <div style={{
                        position:'absolute', top:3, left: form.multiResponse ? 23 : 3,
                        width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left .2s',
                        boxShadow:'0 1px 3px rgba(0,0,0,.2)',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color: form.multiResponse ? '#1d4ed8' : 'var(--text)' }}>
                        {form.multiResponse ? '♾️ Multi-response (Kiosk / Display mode)' : '1️⃣ Single-response (default)'}
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                        {form.multiResponse
                            ? 'Link stays open forever. Perfect for QR codes at exits, counters, reception areas.'
                            : 'Link closes after one submission. Best for personal links sent via SMS or email.'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label>Bill / Order ID <span style={{fontWeight: 400, color: 'var(--text-muted)'}}>(optional)</span></label>
                    <input className="form-control" placeholder="e.g. INV-0042" {...f('billId')} />
                  </div>
                  <div className="form-group">
                    <label>Feedback Form</label>
                    <select className="form-control" {...f('feedbackFormId')}>
                      <option value="">— Audio only —</option>
                      {forms.map(fm => <option key={fm._id} value={fm._id}>{fm.title}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <select className="form-control" {...f('locationId')}>
                      <option value="">— No specific location —</option>
                      {locations.map(l => (
                          <option key={l._id} value={l._id}>{l.name}{l.city ? ` · ${l.city}` : ''}</option>
                      ))}
                    </select>
                    <p style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
                      Assign a location so this feedback appears in location performance reports.
                    </p>
                  </div>
                  {/* Only show recipient fields for non-multi-response */}
                  {!form.multiResponse && (
                      <>
                        <div className="form-group">
                          <label>Send via</label>
                          <select className="form-control" {...f('sendVia')}>
                            {SEND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        {(form.sendVia) && (
                            <div className="grid-2">
                              <div className="form-group">
                                <label>Recipient Name</label>
                                <input className="form-control" placeholder="Customer name" {...f('recipientName')} />
                              </div>
                              {needsEmail && (
                                  <div className="form-group">
                                    <label>Email *</label>
                                    <input className="form-control" type="email" required {...f('recipientEmail')} placeholder="customer@email.com" />
                                  </div>
                              )}
                              {needsPhone && (
                                  <div className="form-group">
                                    <label>Phone *</label>
                                    <input className="form-control" required {...f('recipientPhone')} placeholder="+92 300..." />
                                  </div>
                              )}
                            </div>
                        )}
                      </>
                  )}
                </div>
                <button className="btn btn-primary" type="submit" disabled={creating} style={{marginTop: '2vh'}}>
                  {creating ? 'Creating…' : form.multiResponse ? '🔲 Generate QR Link' : '🔗 Create Link'}
                </button>
              </form>
            </div>
        )}

        {loading ? <div className="spinner" /> : links.length === 0 ? (
            <div className="card text-center" style={{ padding:56 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🔗</div>
              <p className="text-muted">No links yet. Create your first shareable link.</p>
            </div>
        ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {links.map(link => {
                const feedbackUrl = `${BASE_URL}/f/${link.sessionCode}`;
                const isCopied    = copiedId === link._id;
                return (
                    <div key={link._id} className="card" style={{ padding:'16px 18px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>

                        {/* Left — info */}
                        <div style={{ flex:1, minWidth:200 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6 }}>
                      <span className={`badge ${link.status === 'completed' && !link.multiResponse ? 'badge-success' : 'badge-warning'}`}>
                        {link.multiResponse ? '♾️ Kiosk' : link.status}
                      </span>
                            {link.multiResponse && (
                                <span style={{ fontSize:11, color:'#1d4ed8', background:'#dbeafe', borderRadius:6, padding:'2px 8px', fontWeight:600 }}>
                          {link.responseCount || 0} responses
                        </span>
                            )}
                            {link.feedbackForm && (
                                <span style={{ fontSize:11, color:'var(--text-muted)' }}>📋 {link.feedbackForm?.title || 'Form'}</span>
                            )}
                            {link.billId && (
                                <span style={{ fontSize:11, color:'var(--text-muted)' }}>Bill: {link.billId}</span>
                            )}
                          </div>

                          <div style={{ fontSize:12, color:'var(--text-muted)', wordBreak:'break-all', marginBottom:6 }}>
                            {feedbackUrl}
                          </div>

                          {link.recipientName && (
                              <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                                👤 {link.recipientName}
                                {link.recipientEmail && ` · ${link.recipientEmail}`}
                                {link.recipientPhone && ` · ${link.recipientPhone}`}
                              </div>
                          )}

                          {/* Response result for completed single-response links */}
                          {link.response && !link.multiResponse && (
                              <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ fontSize:20 }}>{EMOJI_MAP[link.response.overallSentiment] || '📋'}</span>
                                {link.response.overallSentiment && (
                                    <span style={{ fontSize:12, fontWeight:600, color: SENTIMENT_COLOR[link.response.overallSentiment] }}>
                            {link.response.overallSentiment}
                          </span>
                                )}
                                <span style={{ fontSize:11, color:'var(--text-muted)' }}>
                          {new Date(link.response.createdAt).toLocaleString()}
                        </span>
                              </div>
                          )}
                        </div>

                        {/* Right — actions */}
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'flex-start' }}>
                          {/* QR Code button — always visible */}
                          <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setQrLink(link)}
                              style={{ fontWeight:600, backgroundColor: PRIMARY_COLOR, color: '#fff' }}
                          >
                            🔲 QR Code
                          </button>

                          <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleCopy(link)}
                          >
                            {isCopied ? '✅ Copied' : '📋 Copy'}
                          </button>

                          {/* Resend — only for non-multi, non-completed */}
                          {!link.multiResponse && link.status !== 'completed' && (link.recipientEmail || link.recipientPhone) && (
                              <button
                                  className="btn btn-outline btn-sm"
                                  disabled={resending === link._id}
                                  onClick={() => handleResend(link, link.recipientEmail && link.recipientPhone ? 'both' : link.recipientEmail ? 'email' : 'sms')}
                              >
                                {resending === link._id ? '…' : '🔁 Resend'}
                              </button>
                          )}

                          <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleDelete(link._id)}
                              style={{backgroundColor: 'red', color :'#fff'}}
                          >
                            {'🗑️ Delete'}
                          </button>
                        </div>
                      </div>

                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:8 }}>
                        Created {new Date(link.createdAt).toLocaleString()}
                      </div>
                    </div>
                );
              })}
            </div>
        )}
      </div>
  );
}